import React from 'react';
import { TrendingUp, X, Loader2, AlertCircle } from 'lucide-react';

const REGIME_COLORS = {
    R: 'text-amber-400',
    S: 'text-blue-400',
    F: 'text-emerald-400',
    R_R: 'text-amber-400',
    R_S: 'text-amber-300',
    R_F: 'text-amber-200',
    S_R: 'text-blue-300',
    S_S: 'text-blue-400',
    S_F: 'text-blue-200',
    F_R: 'text-emerald-300',
    F_S: 'text-emerald-200',
    F_F: 'text-emerald-400',
};

const PerformanceRankingsTable = ({
    showRankings,
    setShowRankings,
    startDate,
    endDate,
    rankingsTab,
    setRankingTab,
    rankingsLoading,
    rankingsError,
    rankingsData,
    activeRegimes
}) => {
    if (!showRankings) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-[#1a1d29] border border-gray-800 rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-800 bg-[#14161f]">
                    <div>
                        <h3 className="font-bold text-gray-100 text-base flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-emerald-500" /> Performance Rankings (Historical)
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            Computed from all raw backtest trades outside date range: {startDate} to {endDate}
                        </p>
                    </div>
                    <button onClick={() => setShowRankings(false)} className="p-1.5 hover:bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors cursor-pointer">
                        <X size={18} />
                    </button>
                </div>

                {/* Modal Tabs */}
                <div className="flex border-b border-gray-800 bg-[#14161f] px-4 gap-2">
                    <button
                        className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${rankingsTab === 'strategies' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                        onClick={() => setRankingTab('strategies')}
                    >
                        Strategy Rankings
                    </button>
                    <button
                        className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${rankingsTab === 'regimes' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                        onClick={() => setRankingTab('regimes')}
                    >
                        Regime Rankings
                    </button>
                    <button
                        className={`px-4 py-3 text-xs font-semibold border-b-2 transition-colors cursor-pointer ${rankingsTab === 'combinations' ? 'border-emerald-500 text-emerald-400' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                        onClick={() => setRankingTab('combinations')}
                    >
                        Strategy + Regime Rankings
                    </button>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-auto p-4 custom-scrollbar">
                    {rankingsLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-gray-400 gap-2">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                            <span className="text-sm">Computing rankings from DB...</span>
                        </div>
                    ) : rankingsError ? (
                        <div className="flex flex-col items-center justify-center py-20 text-red-400 gap-2">
                            <AlertCircle className="w-6 h-6" />
                            <span className="text-sm">{rankingsError}</span>
                        </div>
                    ) : !rankingsData ? (
                        <div className="text-center py-20 text-gray-500">No data found.</div>
                    ) : (
                        <div className="overflow-x-auto border border-gray-800 rounded-lg bg-[#14161f]/50">
                            {rankingsTab === 'strategies' && (
                                <table className="w-full text-left border-collapse text-[11px] min-w-[700px]">
                                    <thead>
                                        <tr className="bg-[#14161f] border-b border-gray-800 text-gray-400 uppercase font-bold text-[9px]">
                                            <th className="px-3 py-2 text-center w-[50px]">Rank</th>
                                            <th className="px-3 py-2">Strategy</th>
                                            <th className="px-3 py-2 text-center">Trades</th>
                                            <th className="px-3 py-2 text-center">Accuracy</th>
                                            <th className="px-3 py-2 text-right">Expectancy</th>
                                            <th className="px-3 py-2 text-right">Sharpe</th>
                                            <th className="px-3 py-2 text-right">PnL/DD</th>
                                            <th className="px-3 py-2 text-right">Total PnL</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {rankingsData.strategies.map((row, index) => {
                                            const pnl = Number(row.total_pnl);
                                            const pnlColor = pnl >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold';
                                            const exp = parseFloat(row.expectancy);
                                            const expColor = isNaN(exp) ? 'text-gray-400' : exp >= 0 ? 'text-emerald-400' : 'text-red-400';
                                            return (
                                                <tr key={row.strategy} className="hover:bg-[#202330] transition-colors">
                                                    <td className="px-3 py-2 text-center font-bold text-gray-500">{index + 1}</td>
                                                    <td className="px-3 py-2 font-bold text-blue-400">{row.strategy}</td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.trades}</td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.accuracy}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${expColor}`}>{row.expectancy}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.sharpe}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.pnl_dd}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${pnlColor}`}>
                                                        {pnl >= 0 ? '+' : ''}₹{pnl.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}

                            {rankingsTab === 'regimes' && (
                                <table className="w-full text-left border-collapse text-[11px] min-w-[700px]">
                                    <thead>
                                        <tr className="bg-[#14161f] border-b border-gray-800 text-gray-400 uppercase font-bold text-[9px]">
                                            <th className="px-3 py-2 text-center w-[50px]">Rank</th>
                                            <th className="px-3 py-2 text-center">Regime</th>
                                            <th className="px-3 py-2 text-center">Trades</th>
                                            <th className="px-3 py-2 text-center">Accuracy</th>
                                            <th className="px-3 py-2 text-right">Expectancy</th>
                                            <th className="px-3 py-2 text-right">Sharpe</th>
                                            <th className="px-3 py-2 text-right">PnL/DD</th>
                                            <th className="px-3 py-2 text-right">Total PnL</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {rankingsData.regimes.map((row, index) => {
                                            const pnl = Number(row.total_pnl);
                                            const pnlColor = pnl >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold';
                                            const exp = parseFloat(row.expectancy);
                                            const expColor = isNaN(exp) ? 'text-gray-400' : exp >= 0 ? 'text-emerald-400' : 'text-red-400';
                                            const regimeColor = REGIME_COLORS[row.regime] || 'text-gray-300';
                                            return (
                                                <tr key={row.regime} className="hover:bg-[#202330] transition-colors">
                                                    <td className="px-3 py-2 text-center font-bold text-gray-500">{index + 1}</td>
                                                    <td className={`px-3 py-2 text-center font-bold font-mono ${regimeColor}`}>{row.regime}</td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.trades}</td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.accuracy}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${expColor}`}>{row.expectancy}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.sharpe}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.pnl_dd}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${pnlColor}`}>
                                                        {pnl >= 0 ? '+' : ''}₹{pnl.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}

                            {rankingsTab === 'combinations' && (
                                <table className="w-full text-left border-collapse text-[11px] min-w-[850px]">
                                    <thead>
                                        <tr className="bg-[#14161f] border-b border-gray-800 text-gray-400 uppercase font-bold text-[9px]">
                                            <th className="px-3 py-2 text-center w-[55px]">Rank</th>
                                            <th className="px-3 py-2">Strategy</th>
                                            <th className="px-3 py-2 text-center">Regime</th>
                                            <th className="px-3 py-2 text-center">CE/PE</th>
                                            <th className="px-3 py-2 text-center">Trades</th>
                                            <th className="px-3 py-2 text-center">Accuracy</th>
                                            <th className="px-3 py-2 text-right">Expectancy</th>
                                            <th className="px-3 py-2 text-right">Sharpe</th>
                                            <th className="px-3 py-2 text-right">PnL/DD</th>
                                            <th className="px-3 py-2 text-right">Total PnL</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-800/40 text-gray-300">
                                        {rankingsData.combinations.slice(0, 50).map((row, index) => {
                                            const pnl = Number(row.total_pnl);
                                            const pnlColor = pnl >= 0 ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold';
                                            const exp = parseFloat(row.expectancy);
                                            const expColor = isNaN(exp) ? 'text-gray-400' : exp >= 0 ? 'text-emerald-400' : 'text-red-400';
                                            const regimeColor = REGIME_COLORS[row.regime] || 'text-gray-300';
                                            
                                            const isRowActive = (() => {
                                                const stratActive = activeRegimes[row.strategy];
                                                if (!stratActive) return false;
                                                const regUpper = row.regime.toUpperCase();
                                                const typeActive = stratActive[row.ce_pe.toUpperCase()] || [];
                                                return typeActive.includes(regUpper);
                                            })();

                                            const rowBg = isRowActive 
                                                ? 'bg-emerald-500/5 hover:bg-emerald-500/10' 
                                                : 'hover:bg-[#202330]';

                                            return (
                                                <tr key={row.strategy + '-' + row.regime + '-' + row.ce_pe} className={`transition-colors border-l-2 ${isRowActive ? 'border-emerald-500/50' : 'border-transparent'} ${rowBg}`}>
                                                    <td className="px-3 py-2 text-center font-bold text-gray-500">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <span>{index + 1}</span>
                                                            {isRowActive && (
                                                                <span className="px-1 py-0.2 rounded bg-emerald-500/20 text-emerald-400 text-[8px] font-extrabold uppercase border border-emerald-500/30" title="Currently Active in Regime Grid">Active</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2 font-bold text-blue-400">{row.strategy}</td>
                                                    <td className={`px-3 py-2 text-center font-bold font-mono ${regimeColor}`}>{row.regime}</td>
                                                    <td className="px-3 py-2 text-center">
                                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${row.ce_pe === 'CE' ? 'text-blue-400 bg-blue-500/10 border-blue-500/20' : 'text-orange-400 bg-orange-500/10 border-orange-500/20'}`}>{row.ce_pe || '-'}</span>
                                                    </td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.trades}</td>
                                                    <td className="px-3 py-2 text-center font-mono">{row.accuracy}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${expColor}`}>{row.expectancy}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.sharpe}</td>
                                                    <td className="px-3 py-2 text-right font-mono">{row.pnl_dd}</td>
                                                    <td className={`px-3 py-2 text-right font-mono ${pnlColor}`}>
                                                        {pnl >= 0 ? '+' : ''}₹{pnl.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PerformanceRankingsTable;
